<template>
  <section class="portal-cards" aria-label="Portal links">
    <a
      v-for="portal in portals"
      :key="portal.title"
      class="portal-card"
      :class="portal.theme"
      :href="portal.href"
    >
      <span class="portal-icon" aria-hidden="true">
        <span :class="['icon-shape', portal.icon]"></span>
      </span>
      <span class="portal-title">{{ portal.title }}</span>
      <span class="portal-arrow" aria-hidden="true"></span>
    </a>
  </section>
</template>

<script>
import { resolvePublicPortalLinks } from '@/config/publicPortalLinks'

export default {
  name: 'LandingPortalCards',
  data() {
    const portalLinks = resolvePublicPortalLinks()
    return {
      portals: [
        {
          title: 'Home Health Agencies',
          href: portalLinks.ha.href,
          theme: 'portal-card-health',
          icon: 'icon-home',
        },
        {
          title: 'Clinicians',
          href: portalLinks.clinician.href,
          theme: 'portal-card-clinician',
          icon: 'icon-clinician',
        },
        {
          title: 'Staffing Agencies',
          href: portalLinks.sa.href,
          theme: 'portal-card-staffing',
          icon: 'icon-staffing',
        },
      ],
    }
  },
}
</script>

<style scoped lang="scss">
.portal-cards {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 auto;
  max-width: 1240px;
  padding: 0 32px;
  position: relative;
  z-index: 2;
}

.portal-card {
  align-items: center;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 26px rgba(17, 38, 74, 0.16);
  color: #052a61;
  display: grid;
  gap: 20px;
  grid-template-columns: 92px 1fr 48px;
  min-height: 150px;
  overflow: hidden;
  padding: 28px 24px 28px 34px;
  position: relative;
  text-decoration: none;
}

.portal-card::before {
  bottom: 0;
  content: '';
  left: 0;
  position: absolute;
  top: 0;
  width: 10px;
}

.portal-card:focus,
.portal-card:hover {
  box-shadow: 0 16px 34px rgba(17, 38, 74, 0.22);
  transform: translateY(-1px);
}

.portal-icon {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  height: 84px;
  justify-content: center;
  position: relative;
  width: 84px;
}

.icon-shape {
  display: block;
  opacity: 1;
  position: absolute;
}

.portal-title {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.18;
}

.portal-arrow {
  border: 2px solid currentColor;
  border-radius: 50%;
  height: 42px;
  position: relative;
  width: 42px;
}

.portal-arrow::after {
  border-right: 2px solid currentColor;
  border-top: 2px solid currentColor;
  content: '';
  height: 10px;
  left: 14px;
  position: absolute;
  top: 13px;
  transform: rotate(45deg);
  width: 10px;
}

.portal-card-health {
  color: #079393;
}

.portal-card-health .portal-icon {
  background: rgba(7, 147, 147, 0.12);
}

.portal-card-health::before {
  background: #079393;
}

.portal-card-clinician {
  color: #0b72d9;
}

.portal-card-clinician .portal-icon {
  background: rgba(11, 114, 217, 0.12);
}

.portal-card-clinician::before {
  background: #0b72d9;
}

.portal-card-staffing {
  color: #6f45b7;
}

.portal-card-staffing .portal-icon {
  background: rgba(111, 69, 183, 0.12);
}

.portal-card-staffing::before {
  background: #6f45b7;
}

.icon-home {
  border: 4px solid currentColor;
  border-bottom-width: 5px;
  height: 34px;
  opacity: 1;
  top: 29px;
  transform: rotate(45deg);
  width: 34px;
}

.icon-home::before,
.icon-home::after {
  background: currentColor;
  content: '';
  position: absolute;
}

.icon-home::before {
  height: 17px;
  left: 11px;
  top: 7px;
  transform: rotate(-45deg);
  width: 8px;
}

.icon-home::after {
  height: 8px;
  left: 7px;
  top: 11px;
  transform: rotate(-45deg);
  width: 17px;
}

.icon-clinician {
  border: 3px solid currentColor;
  border-radius: 16px 16px 14px 14px;
  height: 34px;
  opacity: 1;
  top: 18px;
  width: 25px;
}

.icon-clinician::before,
.icon-clinician::after {
  border: 3px solid currentColor;
  content: '';
  position: absolute;
}

.icon-clinician::before {
  border-top: 0;
  border-radius: 0 0 24px 24px;
  height: 38px;
  left: -18px;
  top: 34px;
  width: 55px;
}

.icon-clinician::after {
  border-radius: 50%;
  bottom: -48px;
  height: 7px;
  right: -24px;
  width: 7px;
}

.icon-staffing {
  background: currentColor;
  border-radius: 50%;
  box-shadow: -18px 8px 0 currentColor, 18px 8px 0 currentColor;
  height: 15px;
  opacity: 1;
  top: 27px;
  width: 15px;
}

.icon-staffing::before {
  background: currentColor;
  border-radius: 18px 18px 4px 4px;
  box-shadow: -25px 8px 0 currentColor, 25px 8px 0 currentColor;
  content: '';
  height: 20px;
  left: -8px;
  position: absolute;
  top: 23px;
  width: 31px;
}

@media only screen and (max-width: 1080px) {
  .portal-cards {
    grid-template-columns: 1fr;
    max-width: 620px;
  }
}

@media only screen and (max-width: $mobile-size) {
  .portal-cards {
    gap: 16px;
    padding: 0 18px;
  }

  .portal-card {
    gap: 14px;
    grid-template-columns: 66px 1fr 38px;
    min-height: 112px;
    padding: 20px 18px 20px 28px;
  }

  .portal-icon {
    height: 62px;
    width: 62px;
  }

  .portal-title {
    font-size: 23px;
  }

  .portal-arrow {
    height: 36px;
    width: 36px;
  }
}
</style>
